import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import SEO from "@/components/seo/SEO";
import { SocialShareWidget } from "@/components/SocialShareWidget";
import { blogArticles } from "@/data/sample-content";
import { useParams, Link } from "react-router-dom";
const BlogPost = () => {
  const {
    slug
  } = useParams();
  const post = blogArticles.find(p => p.slug === slug);
  if (!post) {
    return <div className="min-h-screen bg-background">
        <Header />
        <main className="container mx-auto px-4 py-16">
          <h1 className="text-3xl font-bold mb-4">Artikel niet gevonden</h1>
          <p className="text-muted-foreground mb-6">Het opgegeven artikel bestaat niet (meer).</p>
          <Link to="/blog" className="underline hover:text-primary">Terug naar blog</Link>
        </main>
        <Footer />
      </div>;
  }
  const jsonLdArticle = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description: post.excerpt,
    datePublished: post.date,
    author: {
      "@type": "Organization",
      name: post.author
    },
    image: post.imageUrl || undefined,
    keywords: post.keywords.join(", "),
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": typeof window !== "undefined" ? window.location.href : ""
    }
  } as const;
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [{
      "@type": "ListItem",
      position: 1,
      name: "Home",
      item: origin + "/"
    }, {
      "@type": "ListItem",
      position: 2,
      name: "Blog",
      item: origin + "/blog"
    }, {
      "@type": "ListItem",
      position: 3,
      name: post.title,
      item: typeof window !== "undefined" ? window.location.href : ""
    }]
  } as const;
  const related = blogArticles.filter(p => p.slug !== post.slug).map(p => {
    const tagOverlap = p.tags.filter(t => post.tags.includes(t)).length;
    const keywordOverlap = p.keywords.filter(k => post.keywords.includes(k)).length;
    return {
      post: p,
      score: tagOverlap * 2 + keywordOverlap
    };
  }).filter(r => r.score > 0).sort((a, b) => b.score - a.score).slice(0, 3).map(r => r.post);
  return <div className="min-h-screen bg-background">
      <SEO title={`${post.title} | SOL-VI`} description={post.excerpt} type="article" image={post.imageUrl} jsonLd={[jsonLdArticle, breadcrumbSchema]} />
      <Header />
      <main className="container mx-auto px-4 py-12">
        <article>
          <header className="mb-12">
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 bg-primary/20 rounded-full flex items-center justify-center">
                  <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                </div>
                <span className="text-sm font-medium text-primary">Blog artikel</span>
              </div>
              
              <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-6 leading-tight">
                {post.title}
              </h1>
              
              <p className="text-xl text-muted-foreground mb-8 leading-relaxed">
                {post.excerpt}
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row sm:items-center justify-between border-b border-border pb-8 gap-6">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-accent/20 rounded-full flex items-center justify-center">
                    <span className="text-sm font-bold text-accent-foreground">{post.author.charAt(0)}</span>
                  </div>
                  <span className="text-sm font-medium">{post.author}</span>
                </div>
                
                <div className="text-sm text-muted-foreground">
                  {new Date(post.date).toLocaleDateString("nl-NL")} • {post.readTime} minuten leestijd
                </div>
              </div>
              
              <SocialShareWidget title={post.title} description={post.excerpt} size="sm" />
            </div>
          </header>
          {/* Optional hero image */}
          {post.imageUrl}
          <div className="prose prose-lg max-w-none prose-headings:font-bold prose-headings:text-foreground prose-h2:text-2xl prose-h2:mt-12 prose-h2:mb-6 prose-h2:border-b prose-h2:border-border prose-h2:pb-3 prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-4 prose-h3:text-primary prose-p:text-muted-foreground prose-p:leading-relaxed prose-p:mb-6 prose-strong:text-foreground prose-strong:font-semibold prose-ul:space-y-2 prose-li:text-muted-foreground prose-a:text-primary prose-a:font-medium hover:prose-a:underline prose-blockquote:border-l-4 prose-blockquote:border-primary prose-blockquote:bg-accent/10 prose-blockquote:pl-6 prose-blockquote:py-4 prose-blockquote:my-8 prose-blockquote:italic">
            {post.content ? post.content.split("\n\n").map((para, idx) => <p key={idx} dangerouslySetInnerHTML={{
            __html: para.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
          }} />) : <p className="text-muted-foreground">
                Dit artikel heeft nog geen inhoud.
              </p>}
          </div>
        </article>


        {related.length > 0 && <section className="mt-12 border-t border-border pt-8">
            <h2 className="text-2xl font-semibold mb-4">Gerelateerde artikelen</h2>
            <div className="grid gap-6 md:grid-cols-3">
              {related.map(rp => <article key={rp.slug} className="bg-card text-card-foreground border border-border rounded-lg p-4">
                  <h3 className="text-lg font-semibold">
                    <Link to={`/blog/${rp.slug}`} className="hover:text-primary underline">
                      {rp.title}
                    </Link>
                  </h3>
                  <p className="text-xs text-muted-foreground mt-1">
                    {new Date(rp.date).toLocaleDateString("nl-NL")} • {rp.readTime} min
                  </p>
                  <p className="text-sm text-muted-foreground mt-2">{rp.excerpt}</p>
                  <div className="mt-3 text-xs text-muted-foreground">
                    <span>{rp.tags.slice(0, 3).join(" • ")}</span>
                  </div>
                </article>)}
            </div>
          </section>}

        <nav className="mt-12">
          <Link to="/blog" className="underline hover:text-primary">← Terug naar alle artikelen</Link>
        </nav>
      </main>
      <Footer />
    </div>;
};
export default BlogPost;